import React, { useCallback, useEffect, useMemo, useRef } from "react";
import SubTitle from "../SubTitle";
import { DRIVING_ORDER_TYPE, SUB_ORDER_STATUS } from "../../constants";
import { DrivingDetailData } from "../../type";
import MapPopup, { IMapPopupRef } from "./MapPopup";
import useDrawTraceMap, {
  DrawTraceMapProps,
} from "../../../../../../hooks/useDrawTraceMap";
// import { useHistory } from "react-router-dom";
// import { MAP_PAGE_LOCAL_STORAGE_KEY } from "../../../mapView";
import styles from "./index.module.scss";

interface ILocationMapProps {
  detailData: DrivingDetailData;
}

const LocationMap = (props: ILocationMapProps) => {
  // const history = useHistory();
  const { detailData } = props;
  const mapPopupRef = useRef<IMapPopupRef>(null);
  const { containerId, drawTraceMap } = useDrawTraceMap();
  const drawTraceMapPropsRef = useRef<DrawTraceMapProps | null>(null);

  // 行车轨迹: 订单状态为【行驶途中、司机到达目的地、已收车、订单已完成】时显示，否则隐藏
  const isShowTrack = useMemo(() => {
    return [
      SUB_ORDER_STATUS.driving,
      SUB_ORDER_STATUS.arrived,
      SUB_ORDER_STATUS.received,
      SUB_ORDER_STATUS.completed,
    ].includes(Number(detailData?.subOrderStatus));
  }, [detailData]);

  // 订单状态为【已下单、等待司机接单、司机已接单、司机已开启订单、司机已就位、订单取消】时只显示目的地
  const onlyShowDestinationPoint = useMemo(() => {
    return [
      SUB_ORDER_STATUS.ordered,
      SUB_ORDER_STATUS.waitingDriver,
      SUB_ORDER_STATUS.driverAccepted,
      SUB_ORDER_STATUS.driverStarted,
      SUB_ORDER_STATUS.driverArrived,
      SUB_ORDER_STATUS.canceled,
    ].includes(Number(detailData?.subOrderStatus));
  }, [detailData]);

  // 是否是取车单
  const isGetCarOrder = useMemo(
    () =>
      String(detailData?.drivingOrderType) === String(DRIVING_ORDER_TYPE.get),
    [detailData]
  );

  // 取送类型，获取对应目的地信息
  const destinationAddress = useMemo(
    () => (isGetCarOrder ? detailData?.pickAddress : detailData?.returnAddress),
    [detailData, isGetCarOrder]
  );

  const startPoint = useMemo(() => {
    if (onlyShowDestinationPoint) {
      // 只显示目的地时，起点只显示取车单的取车地点，终点不显示
      return isGetCarOrder ? detailData?.pickAddress : null;
    } else {
      return detailData?.traceInfo?.startPoint;
    }
  }, [detailData, isGetCarOrder, onlyShowDestinationPoint]);

  const endPoint = useMemo(() => {
    if (onlyShowDestinationPoint) {
      // 只显示目的地时，终点只显示送车单的还车地点，起点不显示
      return isGetCarOrder ? null : detailData?.returnAddress;
    } else {
      return detailData?.traceInfo?.endPoint;
    }
  }, [detailData, isGetCarOrder, onlyShowDestinationPoint]);

  const initMap = useCallback(async () => {
    if (!!detailData?.traceInfo) {
      const _drawTraceMapProps: DrawTraceMapProps = {
        track: isShowTrack ? detailData?.traceInfo?.drive : null,
        startPoint: startPoint,
        endPoint: endPoint,
      };
      await drawTraceMap(_drawTraceMapProps);
      drawTraceMapPropsRef.current = _drawTraceMapProps;
    }
  }, [detailData, drawTraceMap, endPoint, isShowTrack, startPoint]);

  useEffect(() => {
    initMap();
  }, [initMap]);

  const mapDOM = useMemo(() => {
    return <div className={styles.mapSection} id={containerId} />;
  }, [containerId]);

  // const goToMapViewPage = useCallback(() => {
  //   if (!drawTraceMapPropsRef.current) return;
  //   localStorage.setItem(
  //     MAP_PAGE_LOCAL_STORAGE_KEY,
  //     JSON.stringify(drawTraceMapPropsRef.current)
  //   );
  //   const url = `/serviceOrders/orderDetail/mapView?title=${
  //     isGetCarOrder ? "取车地点" : "还车地点"
  //   }`;
  //   history.push(url);
  // }, [history, isGetCarOrder]);

  return (
    <div className={styles.locationMapComp}>
      <SubTitle>{isGetCarOrder ? "取车地点" : "还车地点"}</SubTitle>
      <div className={styles.mapWrapper}>
        <div
          className={styles.mapContainer}
          onClick={() => {
            if (drawTraceMapPropsRef.current)
              mapPopupRef.current?.open(drawTraceMapPropsRef.current);
          }}
        >
          {mapDOM}
        </div>
        {!!destinationAddress?.address && (
          <div className={styles.address}>{destinationAddress?.address}</div>
        )}
      </div>
      <MapPopup ref={mapPopupRef} />
    </div>
  );
};

export default LocationMap;
